import { useCallback, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, FileSpreadsheet, Trash2, Upload } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  downloadPlotImportTemplateCsv,
  downloadPlotImportTemplateExcel,
  parsePlotImportFile,
  PLOT_IMPORT_SHEET_COLUMNS,
  PLOT_IMPORT_TEMPLATE_EXAMPLE_ROW,
} from '@/lib/plot-import'
import { usePlotImportConfirm, usePlotImportPreview } from '@/hooks/use-plot-import'
import type { PlotImportPreviewRow, PlotImportRowInput } from '@riverpark/types'
import { getApiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'

type Step = 'format' | 'upload' | 'preview' | 'confirm' | 'done'

const STEPS: { id: Step; label: string }[] = [
  { id: 'format', label: 'Format' },
  { id: 'upload', label: 'Upload' },
  { id: 'preview', label: 'Review' },
  { id: 'confirm', label: 'Confirm' },
  { id: 'done', label: 'Done' },
]

function stepIndex(step: Step): number {
  return STEPS.findIndex((s) => s.id === step)
}

function summaryFromPreview(preview: PlotImportPreviewRow[]) {
  return {
    total: preview.length,
    valid: preview.filter((r) => r.status === 'valid').length,
    invalid: preview.filter((r) => r.status === 'invalid').length,
    warnings: preview.filter((r) => r.status === 'warning').length,
  }
}

function statusBadge(status: PlotImportPreviewRow['status']) {
  if (status === 'valid') return <Badge variant="paid">Valid</Badge>
  if (status === 'warning') return <Badge variant="pending">Warning</Badge>
  return <Badge variant="overdue">Invalid</Badge>
}

export function PlotImportWizard({ plotsBasePath }: { plotsBasePath: string }) {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<Step>('format')
  const [rows, setRows] = useState<PlotImportRowInput[]>([])
  const [previewRows, setPreviewRows] = useState<PlotImportPreviewRow[]>([])
  const [summary, setSummary] = useState({ total: 0, valid: 0, invalid: 0, warnings: 0 })
  const [fileError, setFileError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState({ imported: 0, updated: 0 })

  const previewMutation = usePlotImportPreview()
  const importMutation = usePlotImportConfirm()

  const runPreview = useCallback(
    async (inputRows: PlotImportRowInput[]) => {
      setActionError(null)
      const { data } = await previewMutation.mutateAsync(inputRows)
      setPreviewRows(data.rows)
      setSummary(data.summary)
      return data
    },
    [previewMutation],
  )

  async function handleFileSelect(file: File) {
    setFileError(null)
    setActionError(null)
    try {
      const parsed = await parsePlotImportFile(file)
      setRows(parsed)
      await runPreview(parsed)
      setStep('preview')
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Could not read the file')
    }
  }

  function updateRow(rowId: string, patch: Partial<PlotImportRowInput>) {
    setRows((current) => current.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)))
  }

  function removeRow(rowId: string) {
    setRows((current) => current.filter((row) => row.rowId !== rowId))
    setPreviewRows((current) => {
      const next = current.filter((row) => row.rowId !== rowId)
      setSummary(summaryFromPreview(next))
      return next
    })
    setActionError(null)
  }

  async function handleRevalidate() {
    try {
      await runPreview(rows)
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Validation failed'))
    }
  }

  async function handleImport() {
    setActionError(null)
    try {
      const latest = await runPreview(rows)
      if (latest.summary.invalid > 0) {
        setActionError('Fix all invalid rows before importing')
        setStep('preview')
        return
      }
      const { data } = await importMutation.mutateAsync(rows)
      setImportResult({ imported: data.imported, updated: data.updated })
      setStep('done')
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Import failed'))
    }
  }

  const currentStep = stepIndex(step)

  return (
    <PageShell>
      <PageHeader
        label="Management"
        title="Import plots"
        description="Bulk import the plot register from CSV or Excel."
        action={
          <Link
            to={plotsBasePath}
            className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back to plots
          </Link>
        }
      />

      <nav aria-label="Import progress" className="mb-8 flex flex-wrap gap-2">
        {STEPS.map((s, index) => (
          <div
            key={s.id}
            className={cn(
              'rounded border px-3 py-1.5 font-sans text-xs uppercase tracking-wide',
              index <= currentStep
                ? 'border-green/30 bg-green-pale text-status-paid-text'
                : 'border-border-2 bg-card text-muted-foreground',
            )}
          >
            {index + 1}. {s.label}
          </div>
        ))}
      </nav>

      {step === 'format' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Expected file format</h2>
          <p className="mt-2 max-w-2xl font-sans text-sm text-muted-foreground">
            Each row is one plot. Use a plot number for residential plots. For shared areas (church,
            playground, etc.) leave <strong className="text-ink">Plot No.</strong> blank and fill in{' '}
            <strong className="text-ink">Court / Street</strong> or{' '}
            <strong className="text-ink">Notes</strong> — the system assigns an ID such as{' '}
            <code className="text-xs">SPECIAL-CHURCH</code>.
          </p>

          <div className="mt-6 overflow-x-auto rounded border border-border-2">
            <table className="w-full min-w-[720px] border-collapse font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2/50">
                  {PLOT_IMPORT_SHEET_COLUMNS.map((column) => (
                    <th key={column.header} className="px-4 py-2 text-left font-medium text-ink">
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {PLOT_IMPORT_TEMPLATE_EXAMPLE_ROW.map((value, index) => (
                    <td key={PLOT_IMPORT_SHEET_COLUMNS[index]!.header} className="px-4 py-2">
                      {value || <span className="text-muted-foreground">—</span>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <ul className="mt-4 list-inside list-disc space-y-1 font-sans text-sm text-muted-foreground">
            <li>
              <strong className="text-ink">Plot already exists?</strong> Location, notes, and meter
              numbers are updated. Ownership does not change.
            </li>
            <li>
              <strong className="text-ink">Meter columns</strong> — optional; blank cells do not
              clear values already on file.
            </li>
          </ul>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button type="button" variant="secondary" onClick={downloadPlotImportTemplateCsv}>
              <Download className="size-4" aria-hidden />
              Download CSV template
            </Button>
            <Button type="button" variant="secondary" onClick={downloadPlotImportTemplateExcel}>
              <FileSpreadsheet className="size-4" aria-hidden />
              Download Excel template
            </Button>
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="button" onClick={() => setStep('upload')}>
              Continue to upload
            </Button>
          </div>
        </Card>
      )}

      {step === 'upload' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Upload your file</h2>
          <p className="mt-2 font-sans text-sm text-muted-foreground">
            Choose a CSV or Excel file that matches the column layout from the previous step.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFileSelect(file)
            }}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-6 flex w-full flex-col items-center justify-center rounded border-2 border-dashed border-border-2 bg-cream-2/30 px-6 py-12 transition-colors hover:border-green/40 hover:bg-green-pale/20"
          >
            <Upload className="mb-3 size-8 text-muted-foreground" aria-hidden />
            <span className="font-sans text-sm font-medium text-ink">Choose CSV or Excel file</span>
          </button>

          {fileError && (
            <p className="mt-4 font-sans text-sm text-destructive" role="alert">
              {fileError}
            </p>
          )}

          {previewMutation.isPending && (
            <p className="mt-4 font-sans text-sm text-muted-foreground">Parsing and validating…</p>
          )}

          <div className="mt-8 flex justify-between">
            <Button type="button" variant="secondary" onClick={() => setStep('format')}>
              Back
            </Button>
          </div>
        </Card>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <Card className="overflow-hidden p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-2 px-6 py-4">
              <div>
                <h2 className="font-serif text-xl text-ink">Review rows</h2>
                <p className="mt-1 font-sans text-sm text-muted-foreground">
                  {summary.total} rows — {summary.valid} valid, {summary.warnings} warnings,{' '}
                  {summary.invalid} invalid
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleRevalidate()}
                loading={previewMutation.isPending}
              >
                Re-validate
              </Button>
            </div>

            <div className="overflow-x-auto">
              {previewRows.length === 0 ? (
                <p className="px-6 py-8 font-sans text-sm text-muted-foreground">
                  No rows to import.
                </p>
              ) : (
                <table className="w-full min-w-[900px] border-collapse font-sans text-sm">
                  <thead>
                    <tr className="border-b border-border-2 bg-cream-2/50">
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Plot No.</th>
                      <th className="px-4 py-3 text-left">Resolved #</th>
                      <th className="px-4 py-3 text-left">Section</th>
                      <th className="px-4 py-3 text-left">Street</th>
                      <th className="px-4 py-3 text-left">Notes</th>
                      <th className="w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((preview) => {
                      const row = rows.find((r) => r.rowId === preview.rowId)
                      return (
                        <tr key={preview.rowId} className="border-b border-border-2 align-top">
                          <td className="px-4 py-3">{statusBadge(preview.status)}</td>
                          <td className="px-4 py-3">
                            <Input
                              value={row?.plotNumber ?? ''}
                              onChange={(e) =>
                                updateRow(preview.rowId, { plotNumber: e.target.value })
                              }
                              aria-label="Plot number"
                            />
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {preview.newNumber ?? '—'}
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              value={row?.section ?? ''}
                              onChange={(e) =>
                                updateRow(preview.rowId, { section: e.target.value })
                              }
                              aria-label="Section"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              value={row?.courtOrStreet ?? ''}
                              onChange={(e) =>
                                updateRow(preview.rowId, { courtOrStreet: e.target.value })
                              }
                              aria-label="Court or street"
                            />
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {preview.errors.map((msg) => (
                              <p key={msg} className="text-destructive">
                                {msg}
                              </p>
                            ))}
                            {preview.warnings.map((msg) => (
                              <p key={msg}>{msg}</p>
                            ))}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeRow(preview.rowId)}
                              aria-label="Remove row"
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </Card>

          {actionError && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}

          <div className="flex justify-between">
            <Button type="button" variant="secondary" onClick={() => setStep('upload')}>
              Back
            </Button>
            <Button
              type="button"
              disabled={summary.invalid > 0 || summary.total === 0}
              onClick={() => {
                setActionError(null)
                setStep('confirm')
              }}
            >
              Continue to confirm
            </Button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Confirm import</h2>
          <p className="mt-2 font-sans text-sm text-muted-foreground">
            Importing <strong className="text-ink">{summary.valid + summary.warnings}</strong>{' '}
            plots. Existing plots will have location and notes updated only.
          </p>

          {actionError && (
            <p className="mt-4 font-sans text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}

          <div className="mt-8 flex justify-between">
            <Button type="button" variant="secondary" onClick={() => setStep('preview')}>
              Back
            </Button>
            <Button
              type="button"
              loading={importMutation.isPending || previewMutation.isPending}
              onClick={() => void handleImport()}
            >
              Import plots
            </Button>
          </div>
        </Card>
      )}

      {step === 'done' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Import complete</h2>
          <p className="mt-2 font-sans text-sm text-muted-foreground">
            Created <strong className="text-ink">{importResult.imported}</strong> new plots and
            updated <strong className="text-ink">{importResult.updated}</strong> existing plots.
          </p>
          <div className="mt-8 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => navigate(plotsBasePath)}>
              View plots
            </Button>
          </div>
        </Card>
      )}
    </PageShell>
  )
}
